import { useEffect, useRef, useState } from 'react';
import { Plus, X, Users, FileText, Tag, Search, Edit3, Trash2, Check, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, EyeOff, RefreshCw, ChevronDown, ChevronUp, Columns3, Building2, Globe, Loader2, MapPin, Building } from 'lucide-react';
import { Modal } from "@mui/material";
import { motion } from 'framer-motion';

import { showError, showSuccess } from '../utils/toast';
import baseapi from '../services/baseapi';
import { hasPermission } from '../utils/permissionsHelper';
import { jwt_decode } from '../utils/jwt_decode';
import { useClientLevel } from '../hooks/useClientLevel';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SingleSelectLocalSearchApiDropdown from '../components/SingleSelectLocalSearchApiDropdown';
import AgentExportModal from './Agent/AgentExportModal';
import SingleSelectApiDropdown from '../components/SingleSelectApiDropdown';
import LocationSelectorSection, { type LocationIds } from '../components/LocationSelectorSection';

const DEFAULT_REGION = {
    id: 2,
    name: "South India",
    code: "SI"
};

const DEFAULT_STATE = {
    id: 1,
    region_id: 2,
    name: "Andhra Pradesh",
    code: "AP"
};

interface Client {
    id: string;
    name: string;
    display_name: string;
    description: string;
    created: string;
    updated: string;
    total_assets: number;
    total_scans: number;
    ticket_company: any;
    ticket_site: any;
    region_id: number | null;
    region_name: string | null;
    state_id: number | null;
    state_name: string | null;
    district_id: number | null;
    district_name: string | null;
    town_id: number | null;
    town_name: string | null;
    latitude: number | null;
    longitude: number | null;
}

const Clients = () => {
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [editingClient, setEditingClient] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [clients, setClients] = useState<Client[]>([]);
    const [showExportModal, setShowExportModal] = useState(false);
    const [deleteClient, setDeleteClient] = useState<any>({})
    const [open, setOpen] = useState(false)
    const [showAddTown, setShowAddTown] = useState(false);
    const [newTownName, setNewTownName] = useState('');
    const [addingTown, setAddingTown] = useState(false);
    const [townRefreshKey, setTownRefreshKey] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Pagination states
    const [totalItems, setTotalItmes] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Sorting
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    // Column visibility state
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    type ColumnKey =
        | 'name'
        | 'display_name'
        | 'description'
        | 'assets'
        | 'scans'
        | 'created'
        | 'updated'
        | 'location'
        | 'actions';

    // Permissions
    const canCreate = hasPermission('clients', 'create')
    const canUpdate = hasPermission('clients', 'update')
    const canDelete = hasPermission('clients', 'delete')

    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        name: true,
        display_name: true,
        description: true,
        assets: true,
        scans: true,
        created: true,
        updated: true,
        actions: canDelete || canUpdate,
        location: true,
    });

    const decodedToken = jwt_decode();
    const organizationId = decodedToken?.organization_id;

    const [formData, setFormData] = useState({
        name: '',
        display_name: '',
        description: '',
        ticketing_company: null as any,
        ticketing_site: null as any,
        region: DEFAULT_REGION as any,
        state: DEFAULT_STATE as any,
        district: null as any,
        town: null as any,
        latitude: '',
        longitude: '',
    });

    const [isDeskproEnabled, setIsDeskproEnabled] = useState(false);
    const level = useClientLevel();
    const navigate = useNavigate();
    const isFirstSearch = useRef(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [locationIds, setLocationIds] = useState<LocationIds>(() => {
        const rid = searchParams.get('region_id');
        const sid = searchParams.get('state_id');
        const did = searchParams.get('district_id');
        const tid = searchParams.get('town_id');
        return {
            regionId: rid ? (isNaN(Number(rid)) ? rid : Number(rid)) : 2,
            stateId: sid ? (isNaN(Number(sid)) ? sid : Number(sid)) : 1,
            districtId: did ? (isNaN(Number(did)) ? did : Number(did)) : null,
            townId: tid ? (isNaN(Number(tid)) ? tid : Number(tid)) : null,
        };
    });

    useEffect(() => {
        const rid = searchParams.get('region_id');
        const sid = searchParams.get('state_id');
        const did = searchParams.get('district_id');
        const tid = searchParams.get('town_id');

        setLocationIds(prev => {
            const next = {
                regionId: rid ? (isNaN(Number(rid)) ? rid : Number(rid)) : 2,
                stateId: sid ? (isNaN(Number(sid)) ? sid : Number(sid)) : 1,
                districtId: did ? (isNaN(Number(did)) ? did : Number(did)) : null,
                townId: tid ? (isNaN(Number(tid)) ? tid : Number(tid)) : null,
            };
            if (JSON.stringify(prev) !== JSON.stringify(next)) return next;
            return prev;
        });
    }, [searchParams]);

    const handleLocationChange = (newIds: LocationIds) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (newIds.regionId && newIds.regionId !== 2) next.set('region_id', String(newIds.regionId)); else next.delete('region_id');
            if (newIds.stateId && newIds.stateId !== 1) next.set('state_id', String(newIds.stateId)); else next.delete('state_id');
            if (newIds.districtId) next.set('district_id', String(newIds.districtId)); else next.delete('district_id');
            if (newIds.townId) next.set('town_id', String(newIds.townId)); else next.delete('town_id');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    useEffect(() => {
        if (level.name !== 'global') {
            navigate('/dashboard')
        }
        else {
            fetchIntegrations()
        }
    }, []);

    const fetchIntegrations = async () => {
        try {
            const response = await baseapi.get("/integrations")
            if (response.data.status) {
                const deskpro = response.data.data.find((i: any) => i.integration_type === 'deskpro' && i.is_enabled);
                setIsDeskproEnabled(!!deskpro);
            }
        } catch (error) {
            console.error("Error fetching integrations", error);
        }
    }

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    // Re-fetch when debouncedSearch changes
    useEffect(() => {
        if (isFirstSearch.current) {
            isFirstSearch.current = false;
            return;
        }
        fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [debouncedSearch]);

    // Re-fetch when location filters change
    useEffect(() => {
        setCurrentPage(1);
        fetchData(0, itemsPerPage);
    }, [locationIds]);

    const fetchData = async (skip: number = 0, limit: number = 5, orderBy: Record<string, 'asc' | 'desc'>[] = [{ created: 'desc' }]) => {
        try {
            const [sortEntry] = orderBy;
            const sort_by = Object.keys(sortEntry)[0];
            const sort_order = sortEntry[sort_by];

            setLoading(true)

            const queryObj: any = {};
            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { "$ilike": `%${debouncedSearch}%` } },
                    { display_name: { "$ilike": `%${debouncedSearch}%` } }
                ];
            }

            if (locationIds.regionId) queryObj.region_id = locationIds.regionId;
            if (locationIds.stateId) queryObj.state_id = locationIds.stateId;
            if (locationIds.districtId) queryObj.district_id = locationIds.districtId;
            if (locationIds.townId) queryObj.town_id = locationIds.townId;

            const params = {
                query: JSON.stringify(queryObj),
                skip: skip,
                limit: limit,
                sort_by: sort_by,
                sort_order: sort_order
            }
            const response = await baseapi.get("/clients", { params })
            if (response.data.status) {
                setClients(response.data.data)
                setTotalPages(Math.ceil(response.data.total / limit))
                setTotalItmes(response.data.total)
                setLoading(false)
            }
            else {
                showError(response.data.message)
                setLoading(false)
            }
        }
        catch (error: any) {
            showError(error.message)
            setLoading(false)
        }
    }

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                const newDirection = prev.direction === 'asc' ? 'desc' : 'asc';
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: newDirection }]);
                return { key, direction: newDirection };
            } else {
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: 'asc' }]);
                return { key, direction: 'asc' };
            }
        });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) newErrors.name = 'Client name is required';
        if (!formData.region) newErrors.region = 'Region is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.district) newErrors.district = 'District is required';
        if (!formData.town) newErrors.town = 'Town is required';
        if (!formData.latitude) newErrors.latitude = 'Latitude is required';
        if (!formData.longitude) newErrors.longitude = 'Longitude is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => {
            const newData = { ...prev, [field]: value };
            if (field === 'ticketing_company') {
                newData.ticketing_site = null;
            }
            if (field === 'region') {
                newData.state = null;
                newData.district = null;
                newData.town = null;
            }
            if (field === 'state') {
                newData.district = null;
                newData.town = null;
            }
            if (field === 'district') {
                newData.town = null;
            }
            return newData;
        });
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const params: any = {
                name: formData.name,
                display_name: formData.display_name,
                description: formData.description,
                organization_id: Number(organizationId),
                status: "ACTIVE"
            }

            if (isDeskproEnabled) {
                params.ticket_company = formData.ticketing_company ? { id: formData.ticketing_company.id, name: formData.ticketing_company.name || formData.ticketing_company.display_name } : null;
                params.ticket_site = formData.ticketing_site ? { id: formData.ticketing_site.id, name: formData.ticketing_site.name || formData.ticketing_site.display_name } : null;
            }

            params.region_id = formData.region?.id;
            params.state_id = formData.state?.id;
            params.district_id = formData.district?.id;
            params.town_id = formData.town?.id;
            params.latitude = formData.latitude ? parseFloat(formData.latitude) : null;
            params.longitude = formData.longitude ? parseFloat(formData.longitude) : null;

            let response;
            if (editingClient) {
                response = await baseapi.put(`/clients/${editingClient}`, params)
            } else {
                response = await baseapi.post('/clients', params)
            }

            if (response.data.status) {
                showSuccess(response.data.message)
                resetForm();
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage)
                window.dispatchEvent(new CustomEvent('clients-updated'));
            }
            else {
                showError(response.data.message)
            }
        }
        catch (error: any) {
            showError(error.message)
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            display_name: '',
            description: '',
            ticketing_company: null,
            ticketing_site: null,
            region: DEFAULT_REGION,
            state: DEFAULT_STATE,
            district: null,
            town: null,
            latitude: '',
            longitude: '',
        });
        setEditingClient(null);
        setShowAddForm(false);
        setErrors({});
    };

    const handleEdit = (client: Client) => {
        setFormData({
            name: client.name,
            display_name: client.display_name,
            description: client.description,
            ticketing_company: client.ticket_company ? JSON.parse(client.ticket_company) : null,
            ticketing_site: client.ticket_site ? JSON.parse(client.ticket_site) : null,
            region: client.region_id ? { id: client.region_id, name: client.region_name } : null,
            state: client.state_id ? { id: client.state_id, name: client.state_name } : null,
            district: client.district_id ? { id: client.district_id, name: client.district_name } : null,
            town: client.town_id ? { id: client.town_id, name: client.town_name } : null,
            latitude: client.latitude != null ? String(client.latitude) : '',
            longitude: client.longitude != null ? String(client.longitude) : '',
        });
        setEditingClient(client.id);
        setShowAddForm(true);
    };

    const handleDelete = async (client_id: string) => {
        try {
            const response = await baseapi.delete(`/clients/${client_id}`)
            if (response.data.status) {
                showSuccess("Client deleted successfully")
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage)
                window.dispatchEvent(new CustomEvent('clients-updated'));
            }
            else {
                showError(response.data.message)
            }
        }
        catch (error: any) {
            showError(error)
        }
    };

    const handleAddTown = async () => {
        if (!newTownName.trim()) {
            showError('Town name is required');
            return;
        }
        if (!formData.district?.id) {
            showError('Please select a district first');
            return;
        }

        try {
            setAddingTown(true);
            const response = await baseapi.post('/towns', {
                name: newTownName,
                district_id: formData.district.id
            });

            if (response.data.status) {
                showSuccess('Town added successfully');
                setNewTownName('');
                setShowAddTown(false);
                setTownRefreshKey(prev => prev + 1);
            } else {
                showError(response.data.message || 'Failed to add town');
            }
        } catch (error: any) {
            showError(error.message || 'Error occurred while adding town');
        } finally {
            setAddingTown(false);
        }
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        setCurrentPage(1);
        setSearchTerm('');
        setDebouncedSearch('');
        fetchData();
        setTimeout(() => {
            setIsRefreshing(false);
        }, 1000);
    }

    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchData((itemsPerPage * (page - 1)), itemsPerPage)
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchData(0, value)
    };

    const handleColumnToggle = (column: ColumnKey) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const columnOptions: { key: ColumnKey; label: string }[] = [
        { key: 'name', label: 'Name' },
        { key: 'display_name', label: 'Display Name' },
        { key: 'description', label: 'Description' },
        { key: 'created', label: 'Created' },
        { key: 'updated', label: 'Updated' },
        { key: 'location', label: 'Location' },

        ...(canUpdate || canDelete
            ? [{ key: 'actions' as ColumnKey, label: 'Actions' }]
            : []),
    ]

    return (
        <motion.div
        >
            <div className="bg-slate-50 p-3 pb-20 min-h-screen">
                <div className="w-full mx-auto">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2">
                            <div className="bg-[#422568] p-2 rounded-lg shadow-sm">
                                <Building className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h1 className="font-extrabold text-lg text-slate-800 leading-none">Sites</h1>
                            </div>
                            {!loading &&
                                <div className='font-bold text-sm text-slate-500'>
                                    ({totalItems})
                                </div>
                            }
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">

                            <button
                                onClick={handleRefresh}
                                className="rounded-lg bg-white border border-slate-200 text-slate-600 p-2 hover:bg-slate-50 shadow-sm transition-colors"
                            >
                                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            </button>

                            <div className="relative w-full sm:w-64">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search clients..."
                                    className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-700 placeholder-slate-400 shadow-sm focus:border-[#422568] focus:ring-1 focus:ring-[#422568] outline-none transition-all"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>

                            <div className="relative" data-col-menu>
                                <button
                                    onClick={() => setShowColumnFilter(!showColumnFilter)}
                                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 shadow-sm text-slate-700 transition-all flex items-center gap-2"
                                >
                                    <Columns3 className="w-4 h-4" />
                                    <span className="text-[11px] font-bold uppercase tracking-wider">Columns</span>
                                </button>
                                {showColumnFilter && (
                                    <div className="absolute top-full mt-2 right-0 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-[110] p-1.5 max-h-[450px] overflow-y-auto">
                                        <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-100 mb-1">Toggle Columns</div>
                                        {columnOptions.map((option) => (
                                            <button
                                                key={option.key}
                                                onClick={() => handleColumnToggle(option.key)}
                                                className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold uppercase text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    {visibleColumns[option.key] ? (
                                                        <Eye className="w-3.5 h-3.5 text-[#422568]" />
                                                    ) : (
                                                        <EyeOff className="w-3.5 h-3.5 text-slate-400" />
                                                    )}
                                                    {option.label}
                                                </div>
                                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${visibleColumns[option.key] ? 'bg-[#422568] border-[#422568]' : 'border-slate-300'}`}>
                                                    {visibleColumns[option.key] && <Check className="w-3 h-3 text-white" />}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* <button
                                onClick={() => setShowExportModal(true)}
                                className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-4 py-1.5 text-[11px] text-slate-700 shadow-sm font-bold hover:bg-slate-50 transition-all"
                            >
                                <Download className="w-4 h-4 text-cyan-600" />
                                Export
                            </button> */}

                            {canCreate && (
                                <button
                                    onClick={() => {
                                        if (showAddForm) {
                                            resetForm();
                                        } else {
                                            setShowAddForm(true);
                                        }
                                    }}
                                    className="flex items-center gap-1.5 bg-[#422568] rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-[#422568] shadow-sm shadow-[#422568]/20 transition-all"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Site
                                </button>
                            )}
                            <LocationSelectorSection
                                ids={locationIds}
                                onIdsChange={handleLocationChange}
                            />
                        </div>
                    </div>

                    <div className='flex justify-end mb-2'>

                    </div>


                    {/* Table */}
                    <div className="bg-white border border-slate-200 rounded-t-xl overflow-hidden shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs border-collapse">
                                <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                                    <tr>
                                        {columnOptions.map(({ key, label }) => (
                                            visibleColumns[key] && (
                                                <th
                                                    key={key}
                                                    onClick={() => key !== 'actions' && handleSort(key)}
                                                    className={`px-3 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest select-none whitespace-nowrap ${key === 'actions' ? 'text-right' : 'text-left cursor-pointer hover:text-slate-700 transition-colors'}`}
                                                >
                                                    <div className={`flex items-center gap-1.5 ${key === 'actions' ? 'justify-end' : ''}`}>
                                                        {label}
                                                        {key !== 'actions' && (
                                                            sortConfig?.key === key ? (
                                                                sortConfig.direction === 'asc' ? (
                                                                    <ChevronUp size={12} className="text-[#422568]" />
                                                                ) : (
                                                                    <ChevronDown size={12} className="text-[#422568]" />
                                                                )
                                                            ) : (
                                                                <ChevronDown size={12} className="opacity-30" />
                                                            )
                                                        )}
                                                    </div>
                                                </th>
                                            )
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {clients.length === 0 && !loading ? (
                                        <tr>
                                            <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-slate-400 font-bold uppercase tracking-widest text-xs italic">
                                                No clients found
                                            </td>
                                        </tr>
                                    ) : loading ? (
                                        <tr>
                                            <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-slate-500 font-bold uppercase tracking-widest italic">
                                                <div className="flex items-center justify-center gap-2">
                                                    <Loader2 className="w-5 h-5 animate-spin text-[#422568]" />
                                                    Loading clients
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        clients.map((client) => (
                                            <tr key={client.id} className="hover:bg-slate-50 transition-colors group">
                                                {visibleColumns.name && (
                                                    <td className="px-3 py-2">
                                                        <button
                                                            onClick={() => navigate(`/client-details?client_id=${client.id}&tab=assets`)}
                                                            className="text-[12px] text-slate-800 font-bold hover:text-[#422568] transition-colors text-left"
                                                        >
                                                            {client.name}
                                                        </button>
                                                    </td>
                                                )}
                                                {visibleColumns.display_name && (
                                                    <td className="px-3 py-2">
                                                        {client.display_name ? (
                                                            <span className="text-[10px] font-bold bg-slate-100 border border-slate-200 text-slate-600 px-2 py-0.5 rounded uppercase tracking-wider">
                                                                {client.display_name}
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-400">-</span>
                                                        )}
                                                    </td>
                                                )}
                                                {visibleColumns.description && (
                                                    <td className="px-3 py-2 text-slate-500 text-[11px] max-w-xs">
                                                        <div className="truncate" title={client.description}>
                                                            {client.description || '-'}
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.created && (
                                                    <td className="px-3 py-2 text-slate-500 text-[11px]">
                                                        {client.created ? new Date(client.created).toLocaleDateString() : 'N/A'}
                                                    </td>
                                                )}
                                                {visibleColumns.updated && (
                                                    <td className="px-3 py-2 text-slate-500 text-[11px]">
                                                        {client.updated ? new Date(client.updated).toLocaleDateString() : 'N/A'}
                                                    </td>
                                                )}
                                                {visibleColumns.location && (
                                                    <td className="px-4 py-3 text-sm text-slate-700">
                                                        <div className="flex items-center gap-2 text-slate-500 text-[11px]">
                                                            <MapPin className="w-4 h-4 text-[#422568]" />
                                                            <span>{client.region_name || ''}, {client.state_name || ''} , {client.district_name || ''} , {client.town_name || ''}</span>
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.actions && (
                                                    <td className="px-3 py-2 whitespace-nowrap text-right">
                                                        <div className="flex items-center justify-end space-x-1.5">
                                                            {canUpdate && (
                                                                <button
                                                                    onClick={() => handleEdit(client)}
                                                                    className="text-slate-400 hover:text-slate-800 p-1.5 rounded-lg hover:bg-slate-200 transition-colors flex items-center"
                                                                    title="Edit"
                                                                >
                                                                    <Edit3 className="w-4 h-4" />
                                                                </button>)}
                                                            {canDelete && (<button
                                                                onClick={() => {
                                                                    setDeleteClient(client)
                                                                    setOpen(true)
                                                                }}
                                                                className="text-slate-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors flex items-center"
                                                                title="Delete"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>)}
                                                        </div>
                                                    </td>
                                                )}

                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Table Footer */}
                    <div className="bg-slate-50 border border-t-0 rounded-b-xl border-slate-200 px-4 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <select
                                className="px-2.5 py-1.5 bg-white border border-slate-200 rounded text-slate-700 text-xs focus:outline-none focus:border-[#422568] focus:ring-1 focus:ring-[#422568] shadow-sm font-medium"
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                            >
                                <option value={5}>5 Rows</option>
                                <option value={10}>10 Rows</option>
                                <option value={25}>25 Rows</option>
                                <option value={50}>50 Rows</option>
                            </select>
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                                Showing <span className="text-slate-800">{startIndex + 1}</span> - <span className="text-slate-800">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-white rounded border border-transparent hover:border-slate-200 disabled:opacity-40 transition-all"><ChevronsLeft className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-white rounded border border-transparent hover:border-slate-200 disabled:opacity-40 transition-all"><ChevronLeft className="w-4 h-4" /></button>
                            <div className="min-w-[28px] h-[28px] flex items-center justify-center bg-[#422568] rounded-lg text-white text-[12px] font-bold mx-1 shadow-sm">{currentPage}</div>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-white rounded border border-transparent hover:border-slate-200 disabled:opacity-40 transition-all"><ChevronRight className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-white rounded border border-transparent hover:border-slate-200 disabled:opacity-40 transition-all"><ChevronsRight className="w-4 h-4" /></button>
                        </div>
                    </div>

                    {/* Sliding Form Panel from Right */}
                    <div
                        className={`fixed top-12 right-0 sm:right-4 h-[calc(100vh-2rem)] w-full max-w-full sm:max-w-[480px] md:max-w-[520px] lg:max-w-[580px] transition-all duration-500 ease-in-out transform z-51 ${showAddForm ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'}`}
                    >
                        <div className="h-min bg-white shadow-2xl border border-slate-200 rounded-2xl flex flex-col">
                            {/* Form Header */}
                            <div className="flex items-center justify-between px-4 py-4 sm:p-5 border-b border-slate-100">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 sm:p-3 bg-[#422568] border border-[#422568] rounded-xl">
                                        {editingClient ? <Edit3 className="text-white" size={20} /> : <Plus className="text-white" size={20} />}
                                    </div>
                                    <div>
                                        <h2 className="text-lg sm:text-xl font-black text-slate-800 tracking-tight">
                                            {editingClient ? 'Edit Site' : 'Add New Site'}
                                        </h2>
                                        <p className="text-slate-500 text-xs mt-0.5 font-medium">
                                            {editingClient ? 'Update client information' : 'Fill in the details to create a new client'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={resetForm}
                                    className="p-2 sm:p-2.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Form Content */}
                            <div className="flex-1 overflow-y-auto p-5 sm:p-6 bg-slate-50/50">
                                <div className="space-y-5">
                                    {/* Row 1: Client Name & Display Name */}
                                    <div className="flex flex-col md:flex-row gap-5">
                                        <div className="flex-1 w-full">
                                            <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                                                <Users size={14} className="text-[#422568]" />
                                                Site Name <span className="text-red-500 text-sm leading-none">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                placeholder="Enter site name..."
                                                className={`w-full px-3.5 py-2.5 bg-white border rounded-lg text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#422568] shadow-sm ${errors.name ? 'border-red-500 ring-red-100' : 'border-slate-300'}`}
                                            />
                                            {errors.name && (
                                                <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[11px] font-semibold">
                                                    <AlertCircle size={12} />
                                                    {errors.name}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-1 w-full">
                                            <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                                                <Tag size={14} className="text-[#422568]" />
                                                Display Name
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.display_name}
                                                onChange={(e) => handleInputChange('display_name', e.target.value)}
                                                placeholder="Enter display name..."
                                                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 focus:border-[#422568] focus:outline-none focus:ring-2 focus:ring-[#422568] rounded-lg text-slate-800 placeholder-slate-400 text-sm shadow-sm"
                                            />
                                        </div>
                                    </div>

                                    {/* Row 3: Description */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                                            <FileText size={14} className="text-[#422568]" />
                                            Description
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                            placeholder="Enter description..."
                                            rows={3}
                                            className="w-full px-3.5 py-2.5 bg-white border border-slate-300 focus:border-[#422568] focus:outline-none focus:ring-2 focus:ring-[#422568] rounded-lg text-slate-800 placeholder-slate-400 text-sm resize-none shadow-sm"
                                        />
                                    </div>

                                    {isDeskproEnabled && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2 border-t border-slate-200">
                                            <div>
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Ticketing Company"
                                                    icon={Building2}
                                                    apiEndpoint="/ticketing/metadata/companies"
                                                    value={formData.ticketing_company}
                                                    onChange={(val) => handleInputChange('ticketing_company', val)}
                                                    placeholder="Select Company"
                                                    searchInputPlaceholder="Search company..."
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                />
                                            </div>
                                            <div>
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Ticketing Site"
                                                    icon={Globe}
                                                    apiEndpoint="/ticketing/metadata/sites"
                                                    disabled={!formData.ticketing_company}
                                                    value={formData.ticketing_site}
                                                    onChange={(val) => handleInputChange('ticketing_site', val)}
                                                    placeholder={formData.ticketing_company ? "Select Site" : "Select company first"}
                                                    searchInputPlaceholder="Search site..."
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    additionalQueries={{ company_id: formData.ticketing_company?.id }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="pt-2 border-t border-slate-200 space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            {/* <div>
                                                <SingleSelectApiDropdown
                                                    label="Region"
                                                    icon={Globe}
                                                    apiEndpoint="/regions"
                                                    value={formData.region}
                                                    onChange={(val) => handleInputChange('region', val)}
                                                    placeholder="Select Region"
                                                    searchInputPlaceholder="Search region..."
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    required={true}
                                                    disabled={true}
                                                    error={errors.region}
                                                />
                                            </div>
                                            <div>
                                                <SingleSelectApiDropdown
                                                    label="State"
                                                    icon={Globe}
                                                    apiEndpoint="/states"
                                                    disabled={true}
                                                    value={formData.state}
                                                    onChange={(val) => handleInputChange('state', val)}
                                                    placeholder={formData.region ? "Select State" : "Select region first"}
                                                    searchInputPlaceholder="Search state..."
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    additionalQueries={{ region_id: formData.region?.id }}
                                                    required={true}
                                                    error={errors.state}
                                                />
                                            </div> */}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <SingleSelectApiDropdown
                                                    label="District"
                                                    icon={Globe}
                                                    apiEndpoint="/districts"
                                                    disabled={!formData.state}
                                                    value={formData.district}
                                                    onChange={(val) => handleInputChange('district', val)}
                                                    placeholder={formData.state ? "Select District" : "Select state first"}
                                                    searchInputPlaceholder="Search district..."
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    additionalQueries={{ state_id: formData.state?.id }}
                                                    required={true}
                                                    error={errors.district}
                                                />
                                            </div>
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                        <Globe className="w-3.5 h-3.5 text-[#422568]" />
                                                        Town <span className='text-red-500'>*</span>
                                                    </label>
                                                    {!showAddTown ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowAddTown(true)}
                                                            disabled={!formData.district}
                                                            className={`flex items-center gap-1 text-[10px] font-bold transition-colors ${formData.district ? 'text-[#422568] hover:text-[#422568]' : 'text-slate-300 cursor-not-allowed'}`}
                                                        >
                                                            <Plus size={12} />
                                                            ADD
                                                        </button>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5 animate-in slide-in-from-right-2 duration-200">
                                                            <input
                                                                type="text"
                                                                value={newTownName}
                                                                onChange={(e) => setNewTownName(e.target.value)}
                                                                placeholder="Town name"
                                                                className="text-[10px] font-bold px-2 py-0.5 border border-[#422568] rounded-md focus:outline-none focus:ring-1 focus:ring-[#422568] w-24 bg-white"
                                                                autoFocus
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={handleAddTown}
                                                                disabled={addingTown}
                                                                className="text-green-600 hover:text-green-700 transition-colors"
                                                            >
                                                                {addingTown ? <Loader2 size={12} className="animate-spin" /> : <Check size={14} />}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => { setShowAddTown(false); setNewTownName(''); }}
                                                                className="text-red-500 hover:text-red-600 transition-colors"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <SingleSelectApiDropdown
                                                    key={`town-dropdown-${townRefreshKey}`}
                                                    icon={null}
                                                    apiEndpoint="/towns"
                                                    disabled={!formData.district}
                                                    value={formData.town}
                                                    onChange={(val) => handleInputChange('town', val)}
                                                    placeholder={formData.district ? "Select Town" : "Select district first"}
                                                    searchInputPlaceholder="Search town..."
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    additionalQueries={{ district_id: formData.district?.id }}
                                                    error={errors.town}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Latitude & Longitude */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2 border-t border-slate-200">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                                                <MapPin size={14} className="text-[#422568]" />
                                                Latitude
                                                <span className='text-red-500'>*</span>
                                            </label>
                                            <input
                                                type="number"
                                                step="any"
                                                value={formData.latitude}
                                                onChange={(e) => handleInputChange('latitude', e.target.value)}
                                                placeholder="e.g. 12.956861"
                                                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 focus:border-[#422568] focus:outline-none focus:ring-2 focus:ring-[#422568] rounded-lg text-slate-800 placeholder-slate-400 text-sm shadow-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                                                <MapPin size={14} className="text-[#422568]" />
                                                Longitude
                                                <span className='text-red-500'>*</span>
                                            </label>
                                            <input
                                                type="number"
                                                step="any"
                                                value={formData.longitude}
                                                onChange={(e) => handleInputChange('longitude', e.target.value)}
                                                placeholder="e.g. 77.528327"
                                                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 focus:border-[#422568] focus:outline-none focus:ring-2 focus:ring-[#422568] rounded-lg text-slate-800 placeholder-slate-400 text-sm shadow-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Form Footer */}
                            <div className="p-4 sm:p-5 border-t border-slate-100 bg-white rounded-b-2xl">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        className={`w-full sm:flex-1 flex items-center justify-center gap-2 bg-[#422568] text-white py-2.5 px-4 rounded-xl font-bold hover:bg-[#422568] transition-all duration-200 shadow-md shadow-[#422568]/20 text-sm ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {submitting ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Check size={16} />
                                        )}
                                        {submitting ? 'Submitting...' : (editingClient ? 'Update Site' : 'Create Site')}
                                    </button>
                                    <button
                                        onClick={resetForm}
                                        className="w-full sm:w-auto px-6 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 hover:text-slate-800 transition-colors duration-200 font-bold text-sm shadow-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Background Overlay */}
                    <div
                        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 z-50 ${showAddForm ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                        onClick={resetForm}
                    />
                </div>
            </div>

            {/* Click outside to close column filter dropdown */}
            {showColumnFilter && (
                <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowColumnFilter(false)}
                />
            )}

            <AgentExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                templateName="clients"
                clientId={level?.id}
            />

            <Modal open={open} onClose={() => setOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[95%] sm:w-[450px] bg-white border border-slate-200 shadow-2xl p-6 sm:p-8 rounded-2xl text-slate-800 outline-none animate-in zoom-in-95 duration-200">
                    <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-5 border-4 border-white shadow-md">
                            <Trash2 className="w-8 h-8 text-red-500" />
                        </div>

                        <h2 className="text-xl font-black mb-2 tracking-tight text-slate-800">Confirm Delete</h2>

                        <p className="text-slate-500 font-medium text-sm mb-6 leading-relaxed">
                            Are you sure you want to delete client <span className="font-bold text-slate-800">"{deleteClient?.name}"</span>? <br />
                            This will permanently remove the client and all associated data. This action cannot be undone.
                        </p>

                        <div className="flex w-full gap-3">
                            <button
                                onClick={() => setOpen(false)}
                                className="flex-1 px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 hover:text-slate-800 transition-all font-bold text-xs uppercase tracking-wider shadow-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    handleDelete(deleteClient.id)
                                    setOpen(false);
                                }}
                                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-bold text-xs uppercase tracking-wider shadow-md shadow-red-500/20"
                            >
                                Delete Client
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </motion.div>
    );
}

export default Clients;